import { useEffect, useCallback, useState } from 'react';
import { usePlatform } from './usePlatform';

export const useFullscreen = (enabled: boolean = true) => {
  const { isAndroid, canUseFullscreen } = usePlatform();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const enterFullscreen = useCallback(async () => {
    if (!enabled || !canUseFullscreen) return;

    try {
      const element = document.documentElement;
      
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        await (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        await (element as any).msRequestFullscreen();
      }

      setIsFullscreen(true);
      console.log('✅ Fullscreen activado');
    } catch (error) {
      console.warn('⚠️ No se pudo activar fullscreen:', error);
    }
  }, [enabled, canUseFullscreen]);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if ((document as any).webkitFullscreenElement) {
        await (document as any).webkitExitFullscreen();
      }
      setIsFullscreen(false);
      console.log('🚪 Fullscreen desactivado');
    } catch (error) {
      console.warn('⚠️ Error al salir de fullscreen:', error);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !isAndroid) return;

    // Intentar activar fullscreen al cargar
    const timer = setTimeout(() => {
      enterFullscreen();
    }, 1000);

    // Listener para cambios de fullscreen
    const handleFullscreenChange = () => {
      const isFull = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isFull);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Prevenir salida accidental de fullscreen
    const preventExit = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        e.preventDefault();
        console.log('🚫 Escape bloqueado en modo kiosk');
      }
    };

    document.addEventListener('keydown', preventExit);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      document.removeEventListener('keydown', preventExit);
    };
  }, [enabled, isAndroid, enterFullscreen, isFullscreen]);

  return { isFullscreen, enterFullscreen, exitFullscreen };
};
